import React, { useState, useEffect } from 'react';
import { FaBars, FaTimes, FaSun, FaMoon } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../hooks/useAuth';
import useScrollTo from '../hooks/useScrollTo';
import authService from '../services/authService';
import logo from '../assets/images/cititekpro-logo.png';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { isDarkMode, toggleTheme } = useTheme();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { scrollToSection: scrollTo } = useScrollTo();
  const isAdmin = authService.hasAdminAccess();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleNavClick = (item) => {
    if (item.route) {
      navigate(item.route);
    } else {
      scrollTo(item.id);
    }
    setIsOpen(false);
  };

  const menuItems = [
    { id: 'home', label: 'HOME' },
    { id: 'services', label: 'SERVICES' },
    { id: 'our-work', label: 'OUR WORK', route: '/our-work' },
    { id: 'about', label: 'ABOUT' },
    { id: 'consultation', label: 'FREE CONSULTATION' },
  ];

  return (
    <nav
      className={`fixed w-full top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 dark:bg-gray-900/90 backdrop-blur-md shadow-lg"
          : "bg-white dark:bg-gray-900 shadow-md"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-20">
          {/* Logo */}
          <div className="flex-shrink-0 animate-fade-in">
            <button
              onClick={() => handleNavClick({ id: "home" })}
              className="focus:outline-none group relative"
              aria-label="CitiTekPro Home"
            >
              {/* don't include this in the hover - bg-gradient-to-r from-primary to-teal rounded-lg */}
              <div className="absolute inset-0  opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
              <img
                src={logo}
                alt="CitiTekPro Logo"
                className="h-10 w-auto sm:h-12 md:h-14 transition-all duration-300 group-hover:scale-105 relative z-10"
              />
            </button>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-1 lg:space-x-2">
            {menuItems.map((item, index) => (
              <button
                key={item.id}
                onClick={() => handleNavClick(item)}
                className="relative px-3 lg:px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-primary dark:hover:text-teal font-medium transition-all duration-300 group animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <span className="relative z-10">{item.label}</span>
                <span className="absolute inset-0 bg-primary/10 dark:bg-teal/10 rounded-lg scale-0 group-hover:scale-100 transition-transform duration-300"></span>
              </button>
            ))}

            {/* Admin Dashboard Link */}
            {isAuthenticated && isAdmin && (
              <button
                onClick={() => navigate("/admin/dashboard")}
                className="ml-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-300 font-medium animate-fade-in flex items-center"
              >
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"
                  />
                </svg>
                Dashboard
              </button>
            )}

            {/* Dark Mode Toggle */}
            <button
              onClick={toggleTheme}
              className="ml-4 p-2.5 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-all duration-300 group animate-fade-in"
              aria-label="Toggle Dark Mode"
            >
              {isDarkMode ? (
                <FaSun className="text-xl text-yellow-500 group-hover:rotate-180 transition-transform duration-500" />
              ) : (
                <FaMoon className="text-xl text-primary group-hover:-rotate-12 transition-transform duration-500" />
              )}
            </button>
          </div>

          {/* Mobile Menu Button & Dark Mode Toggle */}
          <div className="md:hidden flex items-center space-x-3">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 transition-colors duration-300"
              aria-label="Toggle Dark Mode"
            >
              {isDarkMode ? (
                <FaSun className="text-lg text-yellow-500" />
              ) : (
                <FaMoon className="text-lg text-primary" />
              )}
            </button>
            <button
              onClick={toggleMenu}
              className="p-2 text-gray-700 dark:text-gray-300 hover:text-primary dark:hover:text-teal transition-colors duration-300"
              aria-label="Toggle Menu"
            >
              {isOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-300 ${
          isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-2 pt-2 pb-3 space-y-1 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => handleNavClick(item)}
              className="block w-full text-left px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-primary/10 dark:hover:bg-teal/10 hover:text-primary dark:hover:text-teal transition-all duration-300 font-medium rounded-lg"
            >
              {item.label}
            </button>
          ))}
          {/* Admin Dashboard Link for Mobile */}
          {isAuthenticated && isAdmin && (
            <button
              onClick={() => {
                navigate("/admin/dashboard");
                setIsOpen(false);
              }}
              className="block w-full text-left px-4 py-3 bg-blue-600 text-white hover:bg-blue-700 transition-all duration-300 font-medium rounded-lg flex items-center"
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"
                />
              </svg>
              Dashboard
            </button>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
